import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { RouteMatch, matchRoutes, useLocation, useNavigate, useResolvedPath } from 'react-router-dom';
import { LocalizedService, useLocalizedServiceFactory } from '../../API/GoMoto';
import { Types, useCart, useCartDispatch } from '../../context/cart';
import { useHardwareProviderState } from '../../context/hardware';
import { useFullReset, useKioskBannerImages, useKioskState } from '../../context/Kiosk/kiosk';
import { useLocalization } from '../../context/Localization/localization';
import { usePageState } from '../../context/Page/page';
import { useSavedSessionState, useSavedSessionTrySave } from '../../context/savedSession';
import { GMCarousel } from '../../controls/Carousel';
import { IconButton } from '../../controls/IconButton/IconButton';
import { ImageHelper } from '../../controls/ImageHelper/ImageHelper';
import { LocalizationButtons } from '../../controls/LocalizationButtons/LocalizationButtons';
import { ServiceItem } from '../../controls/ServiceItemList/ServiceItem';
import { ServiceItemList } from '../../controls/ServiceItemList/ServiceItemList';
import { SpgPriceText } from '../../controls/SpgPriceText/SpgPriceText';
import { ThemeImage } from '../../controls/ThemeImage/ThemeImage';
import { isDevelopment, isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { minEstCost, rndMinEstCost } from '../../helpers/minEstCost';
import { VersionInfo } from '../../controls/VersionInfo/VersionInfo';
import css from './Header.module.scss';
import { isVSAKiosk } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { AdminRoutePaths, AppRoutePaths, AppointmentRoutePaths, PathName } from '../../helpers/Routes';
import { GMButton } from '../../controls/Button';
import { GMDialog } from '../../controls/Dialog';
import { GMSizedIcon } from '../../controls/Icon';
import { GMTitle } from '../../controls/Title';
import { CTAButton } from '../../controls/ButtonBase';
import { useDevelopmentDispatch, useDevelopmentState } from '../../context/Development/Development';
import { DevelopmentDispatchTypes } from '../../context/Development/Development.types';

export interface ICustomHeaderProps {
    leftIcons?: any;
    title?: string;
    rightIcons?: any;
}

interface IHeaderProps {
    suppressLocalizationButtons?: boolean;
    suppressBanner?: boolean;
    showSettingsButton?: boolean;
}

const CartIcon = ({ onClick, count, slim }: { onClick: () => void; count: number; slim?: boolean; }) => {
    const { displayCartIcon } = useCart();
    const Localization = useLocalization();

    return (
        displayCartIcon && (
            <div>
                <IconButton
                    headerIcon
                    slim={slim}
                    onClick={onClick}
                    sizedIcon={<GMSizedIcon iconType={'Cart'} iconSize={slim ? 'custom' : 'medium'} />}
                    label={isElectron() ? undefined : Localization.Cart}
                    count={count}
                    title={Localization.ClickToViewCart}
                    ariaLabel={Localization.ClickToViewCart}
                    id={Localization.ClickToViewCart}
                />
            </div>
        )
    );
};

const BackIcon = ({ slim }: { slim?: boolean; }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { pathname } = useResolvedPath('.');
    const Localization = useLocalization();
    const resetKiosk = useFullReset();

    const disableBackRoutes: RouteMatch[] | null = matchRoutes(
        [
            {
                path: '/',
            },
            {
                path: `${pathname}/keydrop`,
            },
            {
                path: `${pathname}/survey1`,
            },
            {
                path: `${pathname}/survey2`,
            },
            {
                path: `${pathname}/advisor`,
            },
            {
                path: `${pathname}/locker`,
            },
            {
                path: `${pathname}/help`,
            },
            {
                path: `${pathname}/doublecheck`,
            },
            {
                path: `${pathname}/complete`,
            },
            {
                path: `${pathname}/keydrop/option`,
            },
            {
                path: `${pathname}/keydrop/advisor`,
            },
            {
                path: `${pathname}/keydrop/locker`,
            },
            {
                path: `${pathname}/keydrop/help`,
            },
            {
                path: `${pathname}/keydrop/doublecheck`,
            },
            {
                path: `${pathname}/keymanagement/keydrop`,
            },
            {
                path: `${pathname}/keymanagement/appointmentdetails/storekey/placekey`,
            },
            {
                path: `${pathname}/keymanagement/appointmentdetails/storekey/complete`,
            },
            {
                path: `${pathname}/keymanagement/storekey/placekey`,
            },
            {
                path: `${pathname}/keymanagement/storekey/complete`,
            },
        ],
        location,
    );

    const disableBackButton: boolean = !!disableBackRoutes && disableBackRoutes.length > 0;

    const makeBackResetRoutes: RouteMatch[] | null = matchRoutes(
        [
            {
                path: `${pathname}/keymanagement`,
            },
        ],
        location,
    );

    const makeBackReset: boolean = !!makeBackResetRoutes && makeBackResetRoutes.length > 0;

    return disableBackButton ? null : (
        <IconButton
            className={css.backIcon}
            headerIcon
            slim={slim}
            onClick={makeBackReset ? resetKiosk : () => navigate(-1)}
            sizedIcon={
                <GMSizedIcon
                    iconType={'Arrow-Back'}
                    iconSize={slim ? 'custom' : 'medium'}
                    ariaLabel={Localization.BackArrow}
                />
            }
            label={isElectron() ? undefined : Localization.Back}
            title={Localization.ClickToGoBack}
            ariaLabel={Localization.ClickToGoBack}
            id={Localization.ClickToGoBack}
        />
    );
};

const SaveIcon = ({ slim }: { slim?: boolean; }) => {
    const savedSessionState = useSavedSessionState();
    const trySave = useSavedSessionTrySave();
    const Localization = useLocalization();

    return savedSessionState.showSaveButton ? (
        <IconButton
            headerIcon
            slim={slim}
            onClick={trySave}
            sizedIcon={<GMSizedIcon iconType={'Save'} iconSize={slim ? 'custom' : 'medium'} />}
            label={Localization.Save}
            title={Localization.ClickToSave}
            ariaLabel={Localization.ClickToSave}
            id={Localization.ClickToSave}
        />
    ) : null;
};

const KioskHeader = (props: { onCartClick: () => void; cartCount: number; suppressLocalizationButtons?: boolean; suppressBanner?: boolean; showSettingsButton?: boolean; }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const resetKiosk = useFullReset();
    const { isLinux } = useHardwareProviderState();
    const Localization = useLocalization();
    const { isDev } = useDevelopmentState();
    const developmentDispatch = useDevelopmentDispatch();
    const {
        dealer: { themeBannerEnabled = true, name: dealerName, stateEstimateEnabled },
        kioskType,
    } = useKioskState();
    const kioskBannerImages =
        useKioskBannerImages()
            .map((src) => (
                <ImageHelper imageProps={{
                    src,
                    title: Localization.formatString(Localization.BannerImage, dealerName) as string,
                }}
                />
            ));
    const isAdmin = location.pathname.includes(AppRoutePaths.Admin);
    const [showVersionInfo, setShowVersionInfo] = useState<boolean>(false);

    const onHeaderClick = () => {
        if (!isAdmin) {
            if (!isLinux || (isLinux && !isVSAKiosk(kioskType))) {
                navigate(AppRoutePaths.Admin);
            }
            else {
                navigate(PathName(AppRoutePaths.Admin, AdminRoutePaths.KioskInformation));
            }
        }
    };

    return (
        <div>
            <GMDialog
                ariaLabel={Localization.VersionInformation}
                open={showVersionInfo}
                title={Localization.VersionInformation}
                actions={[
                    <GMButton
                        id={Localization.Close}
                        title={Localization.Close}
                        ariaLabel={Localization.Close}
                        onClick={() => setShowVersionInfo(false)}
                    >
                        {Localization.Close}
                    </GMButton>,
                ]}
            >
                <VersionInfo />
            </GMDialog>
            {!(location.pathname.includes('/' + AppointmentRoutePaths.DisclaimerTabs) && stateEstimateEnabled) ? (
                <>
                    <div onClick={onHeaderClick} className={css.themeIconBanner}>
                        <ThemeImage themeImageType={'kioskHeaderLogo'} />
                    </div>
                    {!props.suppressBanner ? (
                        <GMCarousel
                            name={'Carousel'}
                            items={[themeBannerEnabled && <ThemeImage themeImageType={'landingPageBanner'} />, ...kioskBannerImages]}
                        />
                    ) : null}
                </>
            ) : null}
            <nav className={css.nav}>
                <div className={css.leftIcons}>
                    <div>
                        <IconButton
                            headerIcon
                            slim
                            onClick={resetKiosk}
                            sizedIcon={<GMSizedIcon iconType={'Home'} iconSize={'custom'} />}
                            ariaLabel={Localization.Home}
                            id={Localization.Home}
                        />
                    </div>
                    <div>
                        <BackIcon slim />
                    </div>
                </div>
                <div className={css.rightIcons}>
                    {isDevelopment() ? (
                        <div style={{
                            marginLeft: '1rem',
                            marginRight: '1rem',
                        }}
                        >
                            <CTAButton
                                hideIcon
                                key={'DevToggle'}
                                className={css.devButton}
                                onClick={() => {
                                    developmentDispatch({
                                        type: DevelopmentDispatchTypes.SetIsDev,
                                        payload: !isDev,
                                    });
                                }}
                                label={isDev ? 'Dev. On' : 'Dev. Off'}
                                styleType={'secondary'}
                                id={isDev ? 'Dev. On' : 'Dev. Off'}
                                ariaLabel={isDev ? 'Dev. On' : 'Dev. Off'}
                            />
                        </div>
                    ) : null}
                    {!props.suppressLocalizationButtons && (
                        <div className={css.localization}>
                            <LocalizationButtons />
                        </div>
                    )}
                    {isLinux && props.showSettingsButton && (
                        <div>
                            <IconButton
                                headerIcon
                                slim
                                onClick={() => navigate(PathName(AppRoutePaths.Admin, AdminRoutePaths.SetupLogin))}
                                sizedIcon={<GMSizedIcon iconType={'Settings'} iconSize={'custom'} />}
                                ariaLabel={Localization.ClickToOpenSettings}
                                id={Localization.ClickToOpenSettings}
                            />
                        </div>
                    )}
                    <CartIcon
                        onClick={props.onCartClick}
                        count={props.cartCount}
                        slim
                    />
                </div>
            </nav>
        </div>
    );
};

const WebHeader = (props: { onCartClick: () => void; cartCount: number; }) => (
    <nav className={css.nav}>
        <div className={css.leftIcons}>
            <div>
                <BackIcon />
            </div>
            <div>
                <SaveIcon />
            </div>
        </div>
        <div className={css.mobileLogo}>
            <ThemeImage themeImageType={'mobileHeaderLogo'} />
        </div>
        <div className={css.rightIcons}>
            <CartIcon onClick={props.onCartClick} count={props.cartCount} />
        </div>
    </nav>
);

export enum CartItemType {
    Service = 1,
    MaintenanceMenu,
    XtimeService,
    XtimeMenu,
    RNDService
}

export const Header = React.memo((props: IHeaderProps) => {
    const { services, maintMenus, xtime, rndServices } = useCart();
    const [showCart, setShowCart] = React.useState(false);
    const [serviceItems, setServiceItems] = React.useState<ICartItem[]>([]);
    const serviceFactory = useLocalizedServiceFactory();
    const { headerProps } = usePageState();
    const Localization = useLocalization();

    useEffect(() => {
        const serviceItems: ICartItem[] = services
            .filter((s) => s.isTransport !== true)
            .map(serviceFactory)
            .map((service) => ({
                name: service.localization.name,
                price: (
                    <SpgPriceText
                        price={service.price}
                        priceLow={service.priceLow}
                        priceHigh={service.priceHigh}
                        spgPricing={service.spgPricing}
                        useSpgPricingLogic={!!service.useSpgPricing}
                    />
                ),
                minEstCost: minEstCost(service) ?? undefined,
                serviceId: service.id,
                type: CartItemType.Service,
            }));

        maintMenus?.forEach((m) => {
            serviceItems.push({
                name: m.title,
                price: <SpgPriceText
                    priceLow={m.priceLow}
                    priceHigh={m.priceHigh}
                    spgPricing={m.spgPricing}
                    showSingleWhenNoRange={false}
                />,
                minEstCost: minEstCost(m) ?? undefined,
                serviceId: m.id,
                scheduleOption: m.scheduleOption,
                type: CartItemType.MaintenanceMenu,
            });
        });

        xtime.services?.forEach((x) => {
            serviceItems.push({
                name: x.name,
                price: x.price,
                minEstCost: x.price,
                serviceId: x.id,
                xtimeType: x.type,
                type: CartItemType.XtimeService,
            });
        });

        xtime.menus?.forEach((x) => {
            serviceItems.push({
                name: x.name,
                price: x.totalPrice,
                minEstCost: x.totalPrice,
                serviceId: x.id,
                type: CartItemType.XtimeMenu,
            });
        });

        rndServices.forEach((rnd) => {
            if (!rnd.doneElsewhere) {
                const localizedService: LocalizedService | undefined = rnd.linkedService ? serviceFactory(rnd.linkedService) : undefined;

                serviceItems.push({
                    opCode: rnd.opCode,
                    name: localizedService && localizedService.localization.name ? localizedService.localization.name : rnd.description,
                    price: (
                        <SpgPriceText
                            price={rnd.price ?? localizedService?.price}
                            priceLow={localizedService?.priceLow}
                            priceHigh={localizedService?.priceHigh}
                            spgPricing={localizedService?.useSpgPricing ? localizedService?.spgPricing : undefined}
                        />
                    ),
                    minEstCost: rndMinEstCost(rnd) ?? undefined,
                    type: CartItemType.RNDService,
                });
            }
        });

        setServiceItems(serviceItems);
    }, [serviceFactory, services, maintMenus, xtime.services, xtime.menus, Localization, rndServices]);

    const renderHeader = () => (
        <>
            {isElectron() ? (
                <KioskHeader
                    onCartClick={() => setShowCart(!showCart)}
                    cartCount={serviceItems.length}
                    suppressLocalizationButtons={props.suppressLocalizationButtons}
                    suppressBanner={props.suppressBanner}
                    showSettingsButton={props.showSettingsButton}
                />
            ) : (
                <WebHeader onCartClick={() => setShowCart(!showCart)} cartCount={serviceItems.length} />
            )}
            <Cart
                items={serviceItems}
                visible={showCart}
                onClose={() => setShowCart(false)}
            />
        </>
    );

    const renderCustomHeader = () => (
        <nav className={classNames(css.nav, css.customHeaderContent)}>
            <div>{headerProps?.leftIcons}</div>
            <div>{headerProps?.title}</div>
            <div>{headerProps?.rightIcons}</div>
        </nav>
    );

    return <header className={css.header}>{headerProps === null ? null : headerProps !== undefined ? renderCustomHeader() : renderHeader()}</header>;
});

Header.displayName = 'Header';

interface ICartItem {
    serviceId?: number;
    name: string;
    price?: number | JSX.Element;
    minEstCost?: number;
    type: CartItemType;
    scheduleOption?: string;
    xtimeType?: 'standard' | 'menu';
    opCode?: string;
}

interface IProps {
    items: ICartItem[];
    visible?: boolean;
    onClose: () => void;
}
const Cart: React.FC<IProps> = React.memo((props) => {
    const Localization = useLocalization();
    // const [visible, setVisible] = useState(props.visible);
    const cartDispatch = useCartDispatch();

    /*
     * useEffect(() => {
     *     if (props.visible) {
     *         setTimeout(() => {
     *             setVisible(true);
     *         }, 200);
     *     } else {
     *         setTimeout(() => {
     *             setVisible(false);
     *         }, 1000);
     *     }
     * }, [props.visible]);
     */

    return props.visible ? (
        <>
            <div className={classNames(css.cart, props.visible ? css.cartVisible : '')}>
                {(!props.items.length && <GMTitle headerLevel={4}>{Localization.YoureCartIsEmpty}</GMTitle>) || (
                    <>
                        <GMTitle headerLevel={4}>{Localization.MyCart}</GMTitle>
                        <ServiceItemList>
                            {props.items
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((item) => (
                                    <ServiceItem
                                        key={item.name + item.price}
                                        title={item.name}
                                        price={item.price}
                                        minEstCost={item.minEstCost}
                                        onDelete={() => {
                                            if (item.type === CartItemType.Service) {
                                                cartDispatch({
                                                    type: Types.RemoveService,
                                                    payload: { id: item.serviceId! },
                                                });
                                            }
                                            else if (item.type === CartItemType.MaintenanceMenu && item.serviceId && item.scheduleOption) {
                                                cartDispatch({
                                                    type: Types.RemoveMaintMenu,
                                                    payload: {
                                                        id: item.serviceId,
                                                        scheduleOption: item.scheduleOption,
                                                    },
                                                });
                                            }
                                            else if (item.type === CartItemType.XtimeService && item.serviceId && item.xtimeType) {
                                                cartDispatch({
                                                    type: Types.RemoveXtimeService,
                                                    payload: {
                                                        id: item.serviceId,
                                                        type: item.xtimeType,
                                                    },
                                                });
                                            }
                                            else if (item.type === CartItemType.XtimeMenu && item.serviceId) {
                                                cartDispatch({
                                                    type: Types.RemoveXtimeMenu,
                                                    payload: { id: item.serviceId },
                                                });
                                            }
                                            else if (item.type === CartItemType.RNDService && item.opCode) {
                                                cartDispatch({
                                                    type: Types.RemoveRNDService,
                                                    payload: { opCode: item.opCode },
                                                });
                                            }
                                        }}
                                    />
                                ))}
                        </ServiceItemList>
                    </>
                )}
            </div>
            <div className={css.cartOverlay} onClick={props.onClose} />
        </>
    ) : null;
});
